/**
 * Hiring Manager Intake — reglas PURAS compartidas (sin I/O): transiciones de
 * estado, qué puede hacer el HM en cada estado, etiqueta de idiomas y el texto
 * plano de los dos documentos (para copiar al portapapeles y para el PDF).
 * Test: backend/src/lib/ta-intake.test.ts (npm run test:ta-intake, en CI).
 */
import type {
  IntakeSummary, JDVersionDoc, JobDescription, LanguageReq, SourcingGuide, TaIntakeStatus,
} from './types/ta-intake';

/** Tono del badge por estado (EnumBadge). */
export const TA_INTAKE_STATUS_TONE: Record<TaIntakeStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'accent' | 'muted'> = {
  INTERVIEWING: 'info',
  JD_REVIEW: 'warning',
  JD_REVISING: 'warning',
  JD_APPROVED: 'accent',
  SAVED: 'success',
  IN_PROCESS: 'accent',
  CLOSED: 'muted',
  ABANDONED: 'muted',
};

/** Transiciones válidas (spec §2 + IN_PROCESS/CLOSED del recruiter). */
const TRANSICIONES: Record<TaIntakeStatus, readonly TaIntakeStatus[]> = {
  INTERVIEWING: ['JD_REVIEW', 'ABANDONED'],
  JD_REVIEW: ['JD_REVISING', 'JD_APPROVED', 'ABANDONED'],
  JD_REVISING: ['JD_REVIEW', 'ABANDONED'],
  JD_APPROVED: ['SAVED', 'ABANDONED'],
  SAVED: ['IN_PROCESS', 'CLOSED'],
  IN_PROCESS: ['SAVED', 'CLOSED'],
  CLOSED: ['IN_PROCESS'],
  ABANDONED: [],
};

export function puedeTransicionar(desde: TaIntakeStatus, hasta: TaIntakeStatus): boolean {
  return TRANSICIONES[desde]?.includes(hasta) ?? false;
}

/** La conversación sigue abierta: el HM puede escribir. */
export function hmPuedeEscribir(status: TaIntakeStatus): boolean {
  return status === 'INTERVIEWING' || status === 'JD_REVIEW' || status === 'JD_REVISING' || status === 'JD_APPROVED';
}

/** El paquete ya quedó para TA (JD congelada, guía guardada). */
export function estaGuardada(status: TaIntakeStatus): boolean {
  return status === 'SAVED' || status === 'IN_PROCESS' || status === 'CLOSED';
}

/** El HM puede archivar (descartar) el borrador. */
export function hmPuedeArchivar(status: TaIntakeStatus): boolean {
  return hmPuedeEscribir(status);
}

/** "Inglés avanzado (excluyente) · Portugués intermedio+ (deseable)" (spec §7). */
export function languagesLabel(langs: LanguageReq[] | null | undefined): string {
  if (!langs?.length) return '';
  return langs
    .map((l) => `${l.language} ${l.level}`.trim() + (l.required ? ' (excluyente)' : ' (deseable)'))
    .join(' · ');
}

// ─── Texto plano de los documentos (copiar / PDF) ───────────────────────────

/**
 * Las etiquetas del Modelo oficial de JD de TA (doc "Job Descroption Model",
 * 29/09/2026). Son las mismas en las dos versiones porque en el modelo están en
 * inglés: lo que cambia entre ESP y ENG es el CONTENIDO, no los rótulos.
 */
export const JD_LABELS = {
  doc: 'JOB DESCRIPTION',
  overview: 'Description:',
  resp: 'Responsibilities:',
  req: 'Requirements:',
  loc: 'Location:',
} as const;

/**
 * La JD como texto, con la estructura del Modelo: nombre del puesto, Description,
 * Responsibilities, Requirements y Location al final.
 *
 * NO salen el área, a quién reporta ni el Impact Level: el agente los sigue
 * recolectando y TA los ve en el resumen del intake, pero el Impact Level es un
 * dato interno y no tiene por qué viajar en el documento que se manda para afuera.
 */
export function jdDocToText(doc: JDVersionDoc, _lang: 'esp' | 'eng'): string {
  const L = JD_LABELS;
  const lines: string[] = [];
  lines.push(L.doc, doc.job_title, '');
  lines.push(L.overview, '');
  for (const p of doc.overview_paragraphs) lines.push(p, '');
  lines.push(L.resp);
  for (const r of doc.responsibilities) lines.push(`- ${r}`);
  lines.push('', L.req);
  for (const r of doc.requirements) lines.push(`- ${r}`);
  lines.push('', `${L.loc} ${doc.location_and_work_model}`);
  return lines.join('\n').trim();
}

export function jdToText(jd: JobDescription): string {
  return `${jdDocToText(jd.esp, 'esp')}\n\n${'='.repeat(60)}\n\n${jdDocToText(jd.eng, 'eng')}`;
}

const titulos = (t: { en: string[]; es: string[]; pt: string[] }) =>
  [['EN', t.en], ['ES', t.es], ['PT', t.pt]]
    .filter(([, v]) => (v as string[]).length)
    .map(([k, v]) => `${k}: ${(v as string[]).join(' · ')}`);

const grupos = (g: SourcingGuide['target_companies']['direct']) =>
  g.map((x) => `- ${x.universe}: ${x.companies.join(', ')}${x.why ? ` — ${x.why}` : ''}`);

/** Las 11 secciones de la guía (spec §33) como texto plano. */
export function guideToText(guide: SourcingGuide, jobTitle: string): string {
  const s: string[] = [];
  const sec = (n: number, t: string, body: string[]) => { s.push(`${n}. ${t}`); s.push(...(body.length ? body : ['—'])); s.push(''); };
  s.push(`Sourcing Guide — ${jobTitle}`, 'Uso interno de Talent Acquisition', '');
  sec(1, 'Perfil objetivo', guide.target_profile.map((x) => `- ${x}`));
  sec(2, 'Must-have', guide.must_have.map((x) => `- ${x}`));
  sec(3, 'Nice-to-have', guide.nice_to_have.map((x) => `- ${x}`));
  sec(4, 'Job Titles para buscar', [
    'Principales', ...titulos(guide.job_titles.primary),
    'Alternativos', ...titulos(guide.job_titles.alternative),
    ...(guide.job_titles.notes ? [`Notas: ${guide.job_titles.notes}`] : []),
  ]);
  sec(5, 'Keywords', [
    `Skills / conocimientos: ${guide.keywords.skills.join(' · ')}`,
    `Industria / modelo de negocio: ${guide.keywords.industry_business_model.join(' · ')}`,
    `Herramientas / plataformas: ${guide.keywords.tools_platforms.join(' · ')}`,
    `Clientes / stakeholders: ${guide.keywords.stakeholders_clients.join(' · ')}`,
  ]);
  sec(6, 'Empresas target', [
    `Mencionadas por el Hiring Manager: ${guide.target_companies.hm_mentioned.length ? guide.target_companies.hm_mentioned.join(', ') : 'ninguna'}`,
    'Targets directos', ...grupos(guide.target_companies.direct),
    'Targets ampliados', ...grupos(guide.target_companies.extended),
  ]);
  sec(7, 'Filtros recomendados de LinkedIn', guide.linkedin_filters.map((f) => `- ${f.filter}: ${f.recommendation}`));
  sec(8, 'Boolean Search', guide.boolean_searches.flatMap((b) => [`${b.name}:`, b.query, ...(b.note ? [`(${b.note})`] : []), '']));
  sec(9, 'Qué mirar en un perfil', guide.profile_signals.map((x) => `- ${x}`));
  sec(10, 'Red flags / falsos positivos', guide.red_flags.map((x) => `- ${x}`));
  sec(11, 'Estrategia de búsqueda recomendada', guide.search_strategy.map((x) => `- ${x}`));
  return s.join('\n').trim();
}

export function intakeSummaryToText(s: IntakeSummary): string {
  const out = [
    `Puesto: ${s.job_title}`,
    `Área / equipo: ${s.area_team}`,
    `Reporta a: ${s.reports_to}`,
    `Impact Level: ${s.impact_level}`,
    `Ubicaciones: ${s.locations.join(', ')}`,
    `Modalidad: ${s.work_model}${s.office_attendance ? ` — ${s.office_attendance}` : ''}`,
    `Idiomas: ${languagesLabel(s.languages) || '—'}`,
  ];
  if (s.team_size_managed != null) out.push(`Equipo a cargo: ${s.team_size_managed}`);
  if (s.opening_reason) out.push(`Motivo de apertura: ${s.opening_reason}`);
  if (s.hm_notes) out.push(`Notas del HM: ${s.hm_notes}`);
  return out.join('\n');
}
